<template>
	<div class="shortcuts-drawer" :class="{ 'is-open': isOpen }" v-if="hasShortcuts">
		<div class="drawer-backdrop" @click="close"></div>
		<div class="drawer-panel" ref="panel" :style="panelDragStyle" :class="{ 'is-dragging': dragging }">
			<button type="button"
			        class="drawer-handle"
			        @click="onHandleClick"
			        @touchstart.passive="onDragStart"
			        @touchmove="onDragMove"
			        @touchend="onDragEnd"
			        @touchcancel="onDragEnd"
			        :aria-expanded="isOpen">
				<span class="handle-grip"></span>
				<i class="fa-solid" :class="isOpen ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
			</button>
			<div class="drawer-content">
				<div class="shortcuts-grid">
					<button type="button" class="shortcut" @click="openOfflineCards"
					        v-if="$store.state.views.offlineCards.length">
						<span class="shortcut-icon">
							<i class="fa-solid fa-credit-card"></i>
						</span>
						<span class="shortcut-label">{{ $t('cards.show_offline') }}</span>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "NativeShortcutsDrawer",
	data() {
		return {
			isOpen: false,
			dragging: false,
			dragStartY: 0,
			dragDeltaY: 0,
			panelHeight: 0,
			wasDragged: false,
		}
	},
	computed: {
		hasShortcuts() {
			return this.$store.state.views.offlineCards.length > 0;
		},
		collapsedOffset() {
			const handleHeight = 80;
			const safeArea = this.$store.state.app.safeArea.bottom || 0;
			return Math.max(0, this.panelHeight - handleHeight - safeArea / 2);
		},
		panelDragStyle() {
			if (!this.dragging) return null;
			const baseY = this.isOpen ? 0 : this.collapsedOffset;
			const y = Math.max(0, Math.min(this.collapsedOffset, baseY + this.dragDeltaY));
			return { transform: `translateY(${y}px)`, transition: 'none' };
		},
	},
	methods: {
		toggle() {
			this.isOpen = !this.isOpen;
		},
		close() {
			this.isOpen = false;
		},
		openOfflineCards() {
			this.close();
			this.$store.commit('SHOW_OFFLINE_CARDS_LIST');
		},
		onHandleClick() {
			if (this.wasDragged) {
				this.wasDragged = false;
				return;
			}
			this.toggle();
		},
		onDragStart(e) {
			this.dragging = true;
			this.wasDragged = false;
			this.dragStartY = e.touches[0].clientY;
			this.dragDeltaY = 0;
			this.panelHeight = this.$refs.panel.offsetHeight;
		},
		onDragMove(e) {
			if (!this.dragging) return;
			const delta = e.touches[0].clientY - this.dragStartY;
			if (this.isOpen) this.dragDeltaY = Math.max(0, delta);
			else             this.dragDeltaY = Math.min(0, delta);
			if (Math.abs(delta) > 5) {
				this.wasDragged = true;
				e.preventDefault();
			}
		},
		onDragEnd() {
			if (!this.dragging) return;
			this.dragging = false;
			const threshold = Math.max(40, this.panelHeight * 0.2);
			if (this.isOpen && this.dragDeltaY > threshold) this.isOpen = false;
			else if (!this.isOpen && -this.dragDeltaY > threshold) this.isOpen = true;
			this.dragDeltaY = 0;
		},
	},
}
</script>

<style scoped lang="scss">
$drawerCollapsedHeight: 80px;
$drawerRadius: 10px;

.shortcuts-drawer {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 80;
	pointer-events: none;
}

.drawer-backdrop {
	position: fixed;
	inset: 0;
	background: transparentize($blackColor, .55);
	opacity: 0;
	visibility: hidden;
	transition: opacity .25s ease, visibility .25s ease;
	pointer-events: none;
}

.shortcuts-drawer.is-open .drawer-backdrop {
	opacity: 1;
	visibility: visible;
	pointer-events: auto;
}

.drawer-panel {
	position: relative;
	background: var(--aside-background-color, #0F1C37);
	border-top-left-radius: $drawerRadius;
	border-top-right-radius: $drawerRadius;
	box-shadow: 0 -8px 24px rgba(0, 0, 0, .18);
	padding-bottom: 0;
	transform: translateY(calc(100% - #{$drawerCollapsedHeight} - var(--safe-area-bottom, 0px) / 2));
	transition: transform .3s cubic-bezier(.22, .61, .36, 1);
	pointer-events: auto;
	max-height: 85vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: var(--primary-gradient, $primaryGradient);
		z-index: 1;
		pointer-events: none;
	}
}

.drawer-panel.is-dragging {
	user-select: none;
}

.shortcuts-drawer.is-open .drawer-panel {
	transform: translateY(0);
}

.drawer-handle {
	appearance: none;
	background: transparent;
	border: 0;
	padding: 0 0 calc(var(--safe-area-bottom, 0px) / 2);
	margin: 0;
	width: 100%;
	height: calc(#{$drawerCollapsedHeight} + var(--safe-area-bottom, 0px) / 2);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	cursor: pointer;
	color: var(--white-color, $whiteColor);
	touch-action: none;

	.handle-grip {
		display: block;
		width: 38px;
		height: 4px;
		border-radius: 2px;
		background: var(--white-color, $whiteColor);
		margin-bottom: 6px;
	}

	i {
		font-size: 12px;
		transition: transform .25s ease;
	}
}

.drawer-content {
	padding: 8px 20px calc(24px + var(--safe-area-bottom, 0px));
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.shortcuts-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 18px 12px;
}

.shortcut {
	appearance: none;
	background: transparent;
	border: 0;
	padding: 6px 2px;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	cursor: pointer;
	transition: transform $animationDefaultDuration;

	&:active {
		transform: scale(.96);
	}
}

.shortcut-icon {
	width: 58px;
	height: 58px;
	border-radius: 14px;
	background: $primaryGradient;
	color: var(--white-color, $whiteColor);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 24px;
	margin-bottom: 8px;
	box-shadow: 0 6px 14px rgba(249, 160, 58, .25);
}

.shortcut-label {
	font-size: 13px;
	font-weight: 500;
	color: var(--white-color, $whiteColor);
	line-height: 1.25;
	max-width: 90px;
}

@media ($mobileS) {
	.shortcut-icon {
		width: 52px;
		height: 52px;
		font-size: 21px;
	}

	.shortcut-label {
		font-size: 12px;
	}
}
</style>
